import { gql, useLazyQuery, useMutation } from '@apollo/client'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Link from '@mui/material/Link'
import Typography from '@mui/material/Typography'
import { ADD_JWT_TO_STATE } from '@promoboxx/redux-stores/src/stores/auth/actions'
import I18n from 'i18n-js'
import { useSnackbar } from 'notistack'
import { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useParams } from 'react-router'

import { jwtRef } from '@src/config/ApolloClient'

import { GET_JWT } from '../auth/saneLogin'
import BusinessSelector from '../shared/BusinessSelector'

export const JOIN_RETAILER = gql`
  mutation joinRetailer($brandId: ID!, $linkId: ID!) {
    joinRetailer(brandId: $brandId, linkId: $linkId) {
      id
    }
  }
`

const GET_RETAILER = gql`
  query getRetailer($id: ID!) {
    getRetailer(id: $id) {
      id
      slug
    }
  }
`

export const CREATE_RETAILER = gql`
  mutation createRetailer($params: CreateRetailerParams!) {
    createRetailer(params: $params) {
      id
      slug
    }
  }
`

export const ADD_RETAILER_TO_RETAILER_ACCOUNT = gql`
  mutation addRetailerToRetailerAccount(
    $params: AddRetailerToRetailerAccountParams!
  ) {
    addRetailerToRetailerAccount(params: $params) {
      id
    }
  }
`

function BusinessSelectDialog(props) {
  const history = useHistory()
  const params = useParams()
  const [createRetailer] = useMutation(CREATE_RETAILER)
  const [addRetailerToRetailerAccount] = useMutation(
    ADD_RETAILER_TO_RETAILER_ACCOUNT,
  )

  const [selectedRetailerAccount, setSelectedRetailerAccount] = useState(null)
  const [isCreating, setIsCreating] = useState(false)
  const { enqueueSnackbar } = useSnackbar()

  const dispatch = useDispatch()
  const [getJwt] = useLazyQuery(GET_JWT, {
    fetchPolicy: 'no-cache',
  })
  const [getRetailer] = useLazyQuery(GET_RETAILER)
  const [joinRetailer] = useMutation(JOIN_RETAILER)
  const headers = useSelector((state) => state.auth.headers)

  const handleRetailerClick = async (retailerAccount) => {
    const existingConnection = retailerAccount.retailers.find((retailer) => {
      return String(retailer.brand.id) === String(props.brandId)
    })

    if (existingConnection) {
      history.push(
        `/retailer/content/${retailerAccount.slug}/${params.brandId}`,
      )
      return
    }
    // this also needs to be updated to map any existing retailer to the user selected retailer account
    // to minimize duplicate retailers, though this works its not ideal
    setSelectedRetailerAccount(retailerAccount)
    setIsCreating(true)

    const addJwtToState = (jwt) => {
      dispatch({
        type: ADD_JWT_TO_STATE,
        payload: {
          jwt,
        },
      })
    }

    try {
      let retailerId = props.retailerId
      let retailerSlug = ''

      if (!retailerId) {
        const newRetailer = {
          name: retailerAccount.name,
          phone: retailerAccount.phone,
          phone_extension: retailerAccount.phone_extension,
          website: retailerAccount.website,
          brand_id: props.brandId,
        }
        const createdRetailer = await createRetailer({
          variables: {
            params: newRetailer,
            retailerAccountId: retailerAccount.id,
          },
        })
        retailerId = createdRetailer.data.createRetailer.id
        retailerSlug = createdRetailer.data.createRetailer.slug
      } else {
        const retailerResponse = await getRetailer({
          variables: {
            id: retailerId,
          },
        })
        await joinRetailer({
          variables: {
            brandId: props.brandId,
            linkId: params.registrationKey,
          },
        })
        retailerId = retailerResponse.data.getRetailer.id
        retailerSlug = retailerResponse.data.getRetailer.slug
      }
      const getJwtResponse = await getJwt({
        variables: {
          accessToken: headers['access-token'],
          client: headers.client,
          expiry: headers.expiry,
          tokenType: headers['token-type'],
          uid: headers.uid,
        },
      })
      addJwtToState(getJwtResponse.data.getJwt.jwt)
      jwtRef.current = getJwtResponse.data.getJwt.jwt
      await addRetailerToRetailerAccount({
        variables: {
          params: {
            retailerAccountId: retailerAccount.id,
            enrollment_method: 'enrollment_flow',
            registration_key_link_id: params.registrationKey,
            retailerId,
          },
        },
      })
      window.location = `/#/register/${params.brandId}/${
        params.registrationKey
      }/locations?addBrand=true&retailerSlug=${retailerSlug}&retailerAccountSlug=${
        retailerAccount.slug
      }&${window.location.search.substring(1)}`
    } catch (err) {
      enqueueSnackbar(
        I18n.t('enrollment.businessSelect.errorWhileConnecting'),
        {
          variant: 'error',
        },
      )
      console.error(err)

      // Intentionally only reset state when something goes wrong, otherwise we
      // reset well before a screen transition happens.
      setIsCreating(false)
      setSelectedRetailerAccount(null)
    }
  }

  const title =
    isCreating && selectedRetailerAccount
      ? I18n.t('enrollment.businessSelect.connectingMessage', {
          brandName: props.brandName,
          retailerAccountName: selectedRetailerAccount.name,
        })
      : I18n.t('enrollment.businessSelect.selectABusiness', {
          brandName: props.brandName,
        })

  const handleClose = () => {
    if (isCreating && selectedRetailerAccount) {
      return
    }

    props.closeDialog()
  }

  return (
    <Dialog fullWidth maxWidth="sm" onClose={handleClose} open={props.open}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <BusinessSelector
          handleRetailerClick={handleRetailerClick}
          working={isCreating && selectedRetailerAccount}
        />
      </DialogContent>
      {!isCreating && (
        <DialogActions>
          <Typography color="textSecondary" variant="body2">
            {I18n.t('enrollment.businessSelect.sellAtDifferentBusiness', {
              brandName: props.brandName,
            })}
            <Link
              href={`/#/register/${params.brandId}/${params.registrationKey}/locations`}
            >
              {I18n.t('enrollment.businessSelect.createNewRetailer')}
            </Link>
          </Typography>
        </DialogActions>
      )}
    </Dialog>
  )
}

export default BusinessSelectDialog
